import { useUserInfo } from "context/user";
import { useAppServices } from "hook/services";
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Search,
  Filter,
  TrendingUp,
  Star,
  Grid,
  List,
  Settings,
  ExternalLink,
  ChevronRight,
  X,
  CheckCircle2,
  Zap,
  Sparkles,
  Clock,
  Eye
} from "lucide-react";

interface Integration {
  _id: string;
  name: string;
  description: string;
  icon: string;
  categories: string[];
  installed: any;
  popular: boolean;
  featured: boolean;
  createdAt?: string;
  updatedAt?: string;
  documentation?: string;
  rating?: number;
  users?: number;
}

const Integrations = () => {
  const AppServices = useAppServices();
  const { user } = useUserInfo();
  const { locationId } = useParams();
  const navigate = useNavigate();
  
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [categories, setCategories] = useState<any[]>([
    { name: "All", _id: "all", icon: <Grid className="w-4 h-4" /> },
    { name: "Recently Added", _id: "recent", icon: <Clock className="w-4 h-4" /> },
  ]);
  const [appCategories, setAppCategories] = useState<any[]>([{ name: "All", _id: "all" }]);
  
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [activeTab, setActiveTab] = useState<"all" | "installed">("all");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);

  const getAppCategories = async () => {
    try {
      const { response } = await AppServices.app.category.filter({});
      if (response) {
        setAppCategories([
          { name: "All", _id: "all" },
          ...response.data.map((cat: any) => ({
            ...cat,
            icon: <div className="w-4 h-4 bg-blue-100 rounded"></div>
          }))
        ]);
      }
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    }
  };

  const getApps = async () => {
    setIsLoading(true);
    try {
      const { response } = await AppServices.app.apps_with_installed({});
      if (response) {
        // Add mock data for demo purposes
        const enhancedData = response.data.map((app: Integration) => ({
          ...app,
          rating: Math.random() * 4 + 1, // Random rating 1-5
          users: Math.floor(Math.random() * 10000) + 1000,
          documentation: "https://docs.example.com",
          createdAt: new Date(Date.now() - Math.random() * 31536000000).toISOString() // Random date within last year
        }));
        setIntegrations(enhancedData);
      }
    } catch (err) {
      console.error("Failed to fetch integrations:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAppCategories();
    getApps();
  }, []);

  const handleConnect = async (integration: Integration, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (integration?.installed) {
        const { response } = await AppServices.app.app_install.delete({
          query: `_id=${integration?.installed?._id}`,
        });
        if (response) {
          getApps();
        }
      } else {
        const { response } = await AppServices.app.app_install.create({
          payload: {
            appId: integration._id,
            companyId: user?.companyId,
            locationId: locationId,
            userId: user?._id,
          },
        });
        if (response) {
          getApps();
        }
      }
    } catch (error) {
      console.error("Failed to toggle integration:", error);
    }
  };

  const handleViewSettings = (integration: Integration, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`./${integration._id}/settings`);
  };

  const filteredIntegrations = useMemo(() => {
    let filtered = integrations;

    // Filter by tab
    if (activeTab === "installed") {
      filtered = filtered.filter(app => app.installed);
    }

    // Filter by category
    if (activeCategory !== "all") {
      switch (activeCategory) {
        case "recent":
          filtered = filtered.sort((a, b) => 
            new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
          );
          break;
        default:
          filtered = filtered.filter(app => 
            app.categories?.includes(activeCategory)
          );
      }
    }

    // Filter by search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(app =>
        app.name.toLowerCase().includes(term) ||
        app.description.toLowerCase().includes(term) ||
        app.categories?.some((cat: string) => cat.toLowerCase().includes(term))
      );
    }

    return filtered;
  }, [integrations, activeTab, activeCategory, searchTerm]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Integrations Marketplace
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
              Connect your favorite tools and services to enhance your workflow. 
              Browse hundreds of integrations to extend your platform's capabilities.
            </p>
            <div className="relative max-w-2xl mx-auto">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search integrations by name, category, or description..."
                className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-600 dark:focus:border-blue-600 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs and Controls */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
          <div className="flex items-center space-x-4">
            <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setActiveTab("all")}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === "all"
                    ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                All Integrations
              </button>
              <button
                onClick={() => setActiveTab("installed")}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === "installed"
                    ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Installed ({integrations.filter(app => app.installed).length})
                </div>
              </button>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded ${viewMode === "grid" ? "bg-white dark:bg-gray-700" : ""}`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded ${viewMode === "list" ? "bg-white dark:bg-gray-700" : ""}`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Category Filter */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Categories</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category._id}
                onClick={() => setActiveCategory(category._id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === category._id
                    ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800"
                    : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                }`}
              >
                {category.icon}
                {category.name}
              </button>
            ))}
            {appCategories.slice(1).map((category) => (
              <button
                key={category._id}
                onClick={() => setActiveCategory(category._id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === category._id
                    ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800"
                    : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-6">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {filteredIntegrations.length} integration{filteredIntegrations.length !== 1 ? 's' : ''} found
          </span>
          {activeCategory !== "all" && (
            <button
              onClick={() => setActiveCategory("all")}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              <X className="w-4 h-4" />
              Clear filter
            </button>
          )}
        </div>

        {/* Loading State */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 animate-pulse">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-2"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
                  </div>
                </div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-5/6 mb-4"></div>
                <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
              </div>
            ))}
          </div>
        ) : filteredIntegrations.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No integrations found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Try adjusting your search or filter criteria
            </p>
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredIntegrations.map((integration) => (
              <div
                key={integration._id}
                onClick={() => navigate(`./${integration._id}/settings`)}
                className="group cursor-pointer bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 hover:shadow-xl hover:border-blue-300 dark:hover:border-blue-900 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl flex items-center justify-center text-2xl group-hover:scale-110 transition-transform duration-300">
                      {integration.icon}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
                          {integration.name}
                        </h3>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex items-center">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-3 h-3 ${
                                star <= Math.floor(integration.rating || 0)
                                  ? "text-amber-400 fill-amber-400"
                                  : "text-gray-300 dark:text-gray-600"
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          ({integration.users?.toLocaleString()}+ users)
                        </span>
                      </div>
                    </div>
                  </div>
                  {integration.installed ? (
                    <div className="flex items-center gap-1 px-2 py-1 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-xs font-medium rounded-full">
                      <CheckCircle2 className="w-3 h-3" />
                      Installed
                    </div>
                  ) : null}
                </div>

                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-2">
                  {integration.description}
                </p>

                <div className="flex flex-wrap gap-2 mb-4">
                  {integration.categories?.slice(0, 2).map((category, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded-md"
                    >
                      {category}
                    </span>
                  ))}
                  {integration.categories && integration.categories.length > 2 && (
                    <span className="px-2 py-1 text-gray-500 dark:text-gray-500 text-xs">
                      +{integration.categories.length - 2}
                    </span>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={(e) => handleConnect(integration, e)}
                    className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                      integration.installed
                        ? "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/30"
                        : "bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md"
                    }`}
                  >
                    {integration.installed ? "Disconnect" : "Connect"}
                  </button>
                  {integration.installed && (
                    <button
                      onClick={(e) => handleViewSettings(integration, e)}
                      className="p-2.5 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      title="Settings"
                    >
                      <Settings className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    </button>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Click to view details
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* List View */
          <div className="space-y-4">
            {filteredIntegrations.map((integration) => (
              <div
                key={integration._id}
                onClick={() => navigate(`./${integration._id}/settings`)}
                className="group cursor-pointer bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 hover:shadow-lg hover:border-blue-300 dark:hover:border-blue-900 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl flex items-center justify-center text-2xl">
                      {integration.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {integration.name}
                        </h3>
                        {integration.featured && (
                          <Sparkles className="w-4 h-4 text-amber-500" />
                        )}
                        {integration.popular && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 text-xs font-medium rounded-full">
                            <Zap className="w-3 h-3" />
                            Popular
                          </div>
                        )}
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">
                        {integration.description}
                      </p>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <div className="flex items-center">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`w-3 h-3 ${
                                  star <= Math.floor(integration.rating || 0)
                                    ? "text-amber-400 fill-amber-400"
                                    : "text-gray-300 dark:text-gray-600"
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            ({integration.users?.toLocaleString()}+ users)
                          </span>
                        </div>
                        <div className="flex gap-2">
                          {integration.categories?.slice(0, 3).map((category, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded-md"
                            >
                              {category}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={(e) => handleConnect(integration, e)}
                      className={`py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                        integration.installed
                          ? "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/30"
                          : "bg-blue-600 text-white hover:bg-blue-700"
                      }`}
                    >
                      {integration.installed ? "Disconnect" : "Connect"}
                    </button>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Call to Action */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 border-t border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Can't find what you're looking for?
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Suggest an integration or build your own with our developer API
            </p>
            <div className="flex justify-center gap-4">
              <button className="px-6 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium">
                <div className="flex items-center gap-2">
                  <ExternalLink className="w-4 h-4" />
                  Suggest Integration
                </div>
              </button>
              <button className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  View API Docs
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Integrations;